/**
 * Habillage de la carte — la seule partie « carte » qui appartient au template.
 *
 * La logique (chargement de l'API, marqueurs par place, recadrage, ballon)
 * vient de `common/components/Map/Map`, comme dans 2025_VOG. Ici on ne décrit
 * que le visuel, qui doit rester ISO avec les références du client :
 *
 *  - **aucun POI Google** : la maquette posait une `<iframe>`
 *    `google.com/maps?q=Tchip+Coiffure,Paris`, donc une *recherche* Google qui
 *    affichait tous les salons de coiffure référencés par Google. On ne veut
 *    voir que les salons du storelocator → `poi` et `transit` en
 *    `visibility: off`, plus `labels.icon` coupé globalement ;
 *  - les couleurs reprennent les tokens de `base.css` (charte Tchip / Groupe
 *    Vog) : fonds gris clair, eau menthe pâle, autoroutes blush, libellés noirs.
 *
 * Les valeurs sont en dur (et pas lues dans les tokens CSS) parce que l'API
 * Google Maps attend des couleurs littérales, pas des `var(--…)`.
 */

/** Tokens de `base.css`, recopiés en littéral pour l'API Google Maps. */
const C = {
  noir: '#1a1a1a',
  gris: '#666666',
  grisClair: '#f5f5f5',
  grisNature: '#efefef',
  blanc: '#ffffff',
  rose: '#f5b8ce',
  roseCanon: '#e84186',
  magentaP: '#fadaea',
  vert: '#47bbc6',
  vertP: '#bbe1e7',
};

/** Style Google Maps : carte propre, aux couleurs de la marque, sans POI. */
export const MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: C.grisClair }] },
  // Coupe toutes les pastilles d'icônes Google (commerces, transports, etc.).
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: C.noir }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: C.blanc }] },

  // ── On ne veut QUE les salons du storelocator ──
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },

  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: C.grisNature }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: C.blanc }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: C.gris }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: C.blanc }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: C.magentaP }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: C.rose }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: C.vertP }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: C.vert }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#e2e2e2' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: C.noir }] },
];

/**
 * Épingles au trait épais noir de la charte (`--border`), en SVG inline :
 * `common/…/Marker.js` fait un `<img src={icon}>`, une data-URI convient donc,
 * et évite un aller-retour réseau et un asset à versionner. Les icônes par
 * défaut de `common` sont les épingles génériques PLACELOOP-GENERIC-2022.
 */
const pin = (fill) =>
  `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="44" viewBox="0 0 32 44">` +
      `<path d="M16 1.5C8.3 1.5 2 7.8 2 15.5c0 9.9 14 27 14 27s14-17.1 14-27c0-7.7-6.3-14-14-14z" ` +
      `fill="${fill}" stroke="${C.noir}" stroke-width="2.5" stroke-linejoin="round"/>` +
      `<circle cx="16" cy="15.5" r="4.6" fill="${C.blanc}" stroke="${C.noir}" stroke-width="2.5"/>` +
      `</svg>`
  )}`;

/** `store` = un salon Tchip (rose canon), `user` = le point recherché (vert). */
export const MAP_MARKERS = {
  store: pin(C.roseCanon),
  user: pin(C.vert),
};

/**
 * Ballon d'info au clic sur une épingle. `common/…/Marker.scss` applique
 * `props.config.balloon.style` en inline sur `.balloon` ; le reste (flèche,
 * ombre) est repris dans `storelocator.css`, qui définit aussi les variables
 * CSS attendues par cette feuille (`--gutter-m`, `--background-color`…).
 */
export const MAP_BALLOON_STYLE = {
  background: C.blanc,
  border: `2.5px solid ${C.noir}`,
  borderRadius: '12px',
  boxShadow: `4px 4px 0 ${C.noir}`,
  fontSize: '12px',
  lineHeight: 1.45,
  fontWeight: 600,
};
